import React, { useState, useLayoutEffect } from "react";

import { Grid, Skeleton } from "@mui/material";
import { VictoryArea, VictoryAxis, VictoryChart, VictoryLabel } from "victory";

import { ChartData, ConsumerMonitoringUnits } from "libLegacy/api/models";

import { AXIS_STYLE, CHART_PADDING, useChartStyles } from "./chartStyles";

import {
  formatCO2eLabel,
  formatDayMonthLabel,
  formatHourLabel,
  formatKwhLabel,
  formatMoneyLabel,
  normalizeTimeValues,
} from "logic/Charts";
import { SelectorGranularitiesProps } from "components/admin/SelectorGranularities";
import { ChartCaptionToggle } from "./ChartCaptionToggle";

export type ChartProsumerMonitoringProps = {
  data: Array<{ name: string; data: Array<ChartData> }>;
  unit?: ConsumerMonitoringUnits;
  granularity: SelectorGranularitiesProps["granularity"];
  isLoading?: boolean;
};

type DataWithVisibility = ChartProsumerMonitoringProps["data"][0] & {
  isVisible: boolean;
  color: string;
};

const formatTimeGranularity = (value, granularity) => {
  switch (granularity) {
    case "day":
      return formatHourLabel(value);
    default:
      return formatDayMonthLabel(value);
  }
};

const formatUnitLabel = (value, unit) => {
  switch (unit) {
    case "kWh":
      return formatKwhLabel(value);
    case "money":
      return formatMoneyLabel(value);
    case "CO2e":
      return formatCO2eLabel(value);
  }
};

const COLOR_SCHEME = [
  "#ED6C02",
  "#1E8BF1",
  "#57B391",
  "#DF6CF6",
  "#E3BB5C",
  "#DE6F6F",
  "#0C4433",
];

const addVisibilityToData = (dataFromProps): DataWithVisibility[] => {
  return dataFromProps
    .filter((category) => category.data?.length)
    .map((dataObj, i) => {
      dataObj["isVisible"] = true;
      dataObj["color"] = COLOR_SCHEME[i];
      return dataObj;
    });
};

export const ChartProsumerMonitoring = ({
  data,
  granularity,
  isLoading,
  unit,
}: ChartProsumerMonitoringProps) => {
  const { classes } = useChartStyles();
  const [displayData, setDisplayData] = useState<Array<DataWithVisibility>>();

  useLayoutEffect(() => {
    if (!isLoading) {
      setDisplayData(addVisibilityToData(data));
    }
  }, [data, isLoading]);

  const handleToggle = (isChecked, itemLabel) => {
    const updatedItems = displayData?.map((item) => {
      if (item.name === itemLabel) {
        item["isVisible"] = isChecked;
      }
      return item;
    });

    setDisplayData(updatedItems);
  };

  const Chart = () => (
    <Grid item container direction="column" className={classes.bordered}>
      <Grid item container>
        <VictoryChart padding={CHART_PADDING} domainPadding={6}>
          {/* @ts-ignore */}
          <VictoryAxis
            style={AXIS_STYLE}
            tickLabelComponent={<VictoryLabel angle={-45} dx={-8} dy={-5} />}
            tickFormat={(point) => formatTimeGranularity(point, granularity)}
          />

          <VictoryAxis
            dependentAxis
            style={AXIS_STYLE}
            tickFormat={(point) => formatUnitLabel(point, unit)}
          />

          {displayData
            ?.filter((item) => item.isVisible)
            .map((dataItem, i) => (
              <VictoryArea
                data={dataItem.data}
                x={normalizeTimeValues}
                y={unit && dataItem?.data[0][unit] ? unit : "value"}
                sortKey="x"
                style={{
                  data: {
                    stroke: dataItem.color,
                    fill: dataItem.color,
                    fillOpacity: 0.2,
                    strokeWidth: "0.752px",
                  },
                }}
              />
            ))}
        </VictoryChart>
      </Grid>

      <Grid container item gap={1} justifyContent="center">
        {displayData?.map(({ name, isVisible, color }, i) => (
          <Grid item justifyContent="center" sx={{ mr: 2 }}>
            <ChartCaptionToggle
              isChecked={isVisible}
              color={color}
              label={name}
              onToggle={handleToggle}
            />
          </Grid>
        ))}
      </Grid>
    </Grid>
  );

  if (isLoading) {
    return (
      <Skeleton variant="rectangular">
        <Chart />
      </Skeleton>
    );
  } else return <Chart />;
};
